import React from "react";
import {
  Avatar,
  Box,
  Group,
  Radio,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Client } from "../../../types/client";
import { MERGE_FIELDS, isSameValue } from "./mergeFields";
import styles from "./MergeFieldsStep.module.css";

interface MergeFieldsStepProps {
  records: Client[];
  /** uuid of the target record; fields default to its values. */
  winningClientId: string;
  /** Per-field map of field key -> chosen record uuid. */
  selections: Record<string, string>;
  onChange: (fieldKey: string, recordId: string) => void;
}

const initials = (c: Client): string =>
  `${c.first_name?.[0] ?? ""}${c.last_name?.[0] ?? ""}`.toUpperCase() || "?";

const shortId = (id: string): string => id.slice(0, 6);

/**
 * Step 2 of the merge wizard. Shows each record side-by-side and lets the user
 * pick the winning value for every field. Fields where all records already
 * agree just show the shared value (no chooser).
 */
const MergeFieldsStep: React.FC<MergeFieldsStepProps> = ({
  records,
  winningClientId,
  selections,
  onChange,
}) => {
  return (
    <Stack gap="lg">
      <Box>
        <Title order={3}>Choose Winning Fields</Title>
        <Text c="dimmed" size="sm">
          Select the correct information to keep for the merged client profile.
        </Text>
      </Box>

      <Table className={styles.table} withTableBorder verticalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={styles.fieldCol}>FIELD</Table.Th>
            {records.map((rec, idx) => (
              <Table.Th key={rec.id}>
                <Group gap="sm" wrap="nowrap" justify="center">
                  <Avatar radius="xl" color="var(--brand-teal)" size={36}>
                    {initials(rec)}
                  </Avatar>
                  <Stack gap={0}>
                    <Text fw={700}>Record {idx + 1}</Text>
                    <Text size="xs" c="dimmed">
                      ID: {shortId(rec.id)}
                    </Text>
                  </Stack>
                </Group>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {MERGE_FIELDS.map((field) => {
            const same = isSameValue(field, records);
            const selected = selections[field.key] ?? winningClientId;

            if (same) {
              return (
                <Table.Tr key={field.key}>
                  <Table.Td className={styles.fieldCol}>{field.label}</Table.Td>
                  <Table.Td colSpan={records.length}>
                    <Text ta="center">{field.format(records[0])}</Text>
                  </Table.Td>
                </Table.Tr>
              );
            }

            return (
              <Table.Tr
                key={field.key}
                role="radiogroup"
                aria-label={`Choose ${field.label}`}
              >
                <Table.Td className={styles.fieldCol}>{field.label}</Table.Td>
                {records.map((rec) => {
                  const isSelected = selected === rec.id;
                  return (
                    <Table.Td key={rec.id}>
                      <Box
                        className={`${styles.choice} ${
                          isSelected ? styles.choiceSelected : ""
                        }`}
                        onClick={() => onChange(field.key, rec.id)}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onChange(field.key, rec.id);
                          }
                        }}
                      >
                        <Radio
                          checked={isSelected}
                          onChange={() => onChange(field.key, rec.id)}
                          // Stop the click bubbling to the Box's onClick — the
                          // Radio's own onChange already records the choice, so
                          // letting it bubble would just fire onChange twice.
                          onClick={(e) => e.stopPropagation()}
                          color="var(--brand-teal)"
                          aria-label={`Use ${field.label} from record ${rec.id}`}
                        />
                        <Text>{field.format(rec)}</Text>
                      </Box>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

export default MergeFieldsStep;
